// Payout address book.
//
// The control this screen exists to make visible: an address is PROPOSED here,
// and it is payable only once the Books approve it (the operator, 22 Oct 2026,
// N-061: "a whitelist mechanism in books … safer since we are doing it in
// single key in hot and merchant hot"). That two-step is what stops a
// compromised app layer adding its own destination and paying itself — so
// "Awaiting the Books" is rendered as a state that blocks money, not as a
// soft warning.
//
// On the design system (22 Oct 2026): the name is the figure, the address
// its second line; every state one line.
//
// Screens' round 2 (23 Oct 2026): the Pay sheet and the propose door are
// mounted outside the list's loading, failed and empty branches — a refresh
// that fails while a payout is queued keeps the sheet, its result and its
// request id — and a list read once stays on screen through a failed refresh.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { whitelistGroup, whitelistPollMs, expectedPending, primaryReceivedId, landedAtOf, listState, paySources, type WhitelistGroup, type StageExpected } from '../../lib/custody-screen';
import { FIELD, SELECT } from './field';
import { receivedRefusal } from './refusals';
import { ReadFailed } from './ReadFailed';
import { isCustodySignin } from '../../lib/errors';
import { custody as api, type WhitelistEntry } from '../../lib/api';
import { shortAddress, relativeTime, until, groupDigits } from '../../lib/custody-format';
import { Card, StateLine, EmptyState, Pending, Pill, Value, MoreDetails } from '../../components/kit';
import { ShieldCheck, ShieldAlert, Plus, Send } from 'lucide-react';
import { MoveSheet } from './MoveSheet';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

/**
 * One payout address, by its stage (db/0098; the operator, 24 Oct 2026: "integrate this in a whitelisting rule,
 * so whitelisting itself includes this test"): Approved → Test sent → Whitelisted. The stage is the server's word;
 * the row says it in one line and offers the one thing to do — send the test, press Received, or pay.
 */
function Entry({ e, onPay, testUsdt, primary }: { e: WhitelistEntry; onPay: (e: WhitelistEntry) => void; testUsdt: string; primary: boolean }) {
  const qc = useQueryClient();
  const group = whitelistGroup(e);
  // Provenance: what approved it. Whether it can be paid is the stage's word.
  const approvedByBooks = Boolean(e.approvedAt) && e.approvedVia === 'books';
  const disabled = e.status !== 'active';
  // The listing's landing time: the stamp, or the swept test before its stamp — Received shows either way (re-review #1).
  const landedAt = landedAtOf(e);
  const landed = landedAt !== null;
  const test = `${groupDigits(testUsdt)} USDT`;
  const received = useMutation({
    mutationFn: () => api.payoutTestReceived(e.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] }); },
  });
  const pill = disabled ? <Pill tone="bad">{e.status}</Pill>
    : group === 'payable' ? <Pill tone="good" icon={<ShieldCheck size={11} />}>Whitelisted</Pill>
    : group === 'approved' ? <Pill>Approved</Pill>
    : group === 'test_sent' ? <Pill tone="warn">Test sent</Pill>
    : group === 'unknown' ? <Pill tone="bad">Not known</Pill>
    : <Pill tone="warn" icon={<ShieldAlert size={11} />}>Proposed</Pill>;
  return (
    <Card tone={disabled ? 'bad' : group === 'payable' || group === 'approved' ? undefined : 'warn'}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Value strong>{e.partyName}</Value>
          <div className="font-mono text-meta text-tg-hint">
            {/* The head only: the ending is what the signing sheet asks him to type (audit of 21 Oct, E3). */}
            {shortAddress(e.address, 12, 0)}
          </div>
          {e.label && <div className="mt-0.5 text-meta text-tg-hint">{e.label}</div>}
        </div>
        <span className="flex items-center gap-1">
          {!disabled && group === 'payable' && e.partyRole === 'merchant' && <Pill>Merchant</Pill>}
          {pill}
        </span>
      </div>

      <div className="mt-2 space-y-1">
        {e.kytVerdict && (
          <div className="row">
            <span className="label">KYT</span>
            <span className={`text-body ${e.kytVerdict === 'clear' ? '' : 'text-neg'}`}>
              {e.kytVerdict}
              {e.kytExpiresAt && <span className="text-tg-hint"> · expires {until(e.kytExpiresAt)}</span>}
            </span>
          </div>
        )}
        {approvedByBooks && (
          <div className="row">
            <span className="label">Approved</span>
            <span className="text-body">in the Books by {e.approvedBy ?? '—'} · {relativeTime(e.approvedAt!)}{e.approvedByOwner === false ? ' · not the owner' : ''}</span>
          </div>
        )}
        {group === 'awaiting' && e.proposedBy && (
          <div className="row">
            <span className="label">Proposed</span>
            <span className="text-body text-tg-hint">by {e.proposedBy}</span>
          </div>
        )}
        {e.testReceivedAt && (
          <div className="row">
            <span className="label">Test</span>
            <span className="text-body text-tg-hint">received · {relativeTime(e.testReceivedAt)} · by {e.testReceivedBy ?? '—'}</span>
          </div>
        )}
      </div>

      {!disabled && group === 'awaiting' && (
        <StateLine tone="warn" className="mt-3" more={approvedByBooks ? 'Approved in the Books by someone who is not the owner: only the OWNER\'s Approve payout address starts the whitelisting.' : e.approvedAt ? 'An earlier approval from the desk IP or a Ledger message no longer counts: only the Books\' Approve payout address starts the whitelisting.' : 'Approve payout address in the Books; then a 10 USDT test the customer confirms whitelists it.'}>
          Awaiting the Books' approval — nothing can be paid.
        </StateLine>
      )}

      {/* No stage in the read: nothing is offered until it is read (Screens' round on db/0098, #3). */}
      {!disabled && group === 'unknown' && (
        <StateLine tone="bad" className="mt-3" more="The address book was read without this address's whitelisting stage, so the screen cannot tell what may be paid to it. The desk's own rule still decides any payment; refresh, and tell the wallet session if it stays.">
          Stage not read — nothing can be paid here yet.
        </StateLine>
      )}

      {/* Stage 1 done (db/0098): only the test can go. */}
      {!disabled && group === 'approved' && (
        <>
          <StateLine className="mt-3" more={`Whitelisting has two steps: the owner's approval in the Books, then a ${test} test the customer confirms to you. Until you press Received for it, the test is the only payment this address can receive.`}>
            Send the {test} test first.
          </StateLine>
          <button onClick={() => onPay(e)} className="btn-secondary mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 text-body">
            <Send className="h-3.5 w-3.5" /> Send the {test} test
          </button>
        </>
      )}

      {/* Stage 2 (db/0098): on its way, or landed and waiting for the operator's Received. */}
      {!disabled && group === 'test_sent' && !landed && (
        <StateLine className="mt-3" more="Sign it in Approvals if you have not; it lands about a minute after it is sent. Nothing else can be paid to this address until the customer confirms it and you press Received.">
          The {test} test is on its way.
        </StateLine>
      )}
      {!disabled && group === 'test_sent' && landed && (
        <>
          <StateLine tone="warn" className="mt-3" more={`Landed ${relativeTime(landedAt!)}${e.testTxHash ? ` · tx ${shortAddress(e.testTxHash, 8, 6)}` : ''}. Ask the customer whether the ${test} arrived — by Telegram, phone or in person — and press Received only when they confirm. Not received? Do not press: revoke the address in the Books.`}>
            Test landed — press Received once the customer confirms.
          </StateLine>
          <button
            onClick={() => received.mutate()}
            disabled={received.isPending}
            className={`${primary ? 'btn-primary' : 'btn-secondary'} mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 text-body`}
          >
            <ShieldCheck className="h-3.5 w-3.5" /> {received.isPending ? 'Saving…' : 'Received'}
          </button>
          {received.isError && <StateLine tone="bad" className="mt-1" more={textOf(received.error)}>{receivedRefusal(received.error)}</StateLine>}
        </>
      )}

      {/* Whitelisted: a payout from Hot, or for a merchant also from Merchant Hot or Flagged. The row it queues is
          built by the desk and signed in Approvals — nothing moves here. */}
      {!disabled && group === 'payable' && (
        <button
          onClick={() => onPay(e)}
          className="btn-secondary mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 text-body"
        >
          <Send className="h-3.5 w-3.5" /> Pay
        </button>
      )}
    </Card>
  );
}

/** A TRON address is 34 characters and starts with T; the server checks the checksum. */
const TRON_SHAPE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

function ProposeForm({ onDone }: { onDone: () => void }) {
  const [partySlug, setPartySlug] = useState('');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const qc = useQueryClient();
  const customers = useQuery({ queryKey: ['custody', 'customers'], queryFn: api.customers });

  const m = useMutation({
    mutationFn: () => api.proposeWhitelist({ partySlug, chain: 'TRC20', address: address.trim(), label: label || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] }); onDone(); },
  });
  const trimmed = address.trim();
  const shapeOk = trimmed === '' || TRON_SHAPE.test(trimmed);

  return (
    <Card title="Propose a payout address">
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-meta text-tg-hint">Customer</span>
          <select className={`${SELECT} w-full`} value={partySlug} onChange={(e) => setPartySlug(e.target.value)}>
            <option value="">Choose a customer…</option>
            {(customers.data?.customers ?? []).map((c) => (
              <option key={c.slug} value={c.slug}>{c.name}{c.role === 'merchant' ? ' · merchant' : ''}</option>
            ))}
          </select>
          {customers.isError && !isCustodySignin(customers.error) && <StateLine tone="bad" className="mt-1" more={textOf(customers.error)}>Couldn't read the customers — try again.</StateLine>}
        </label>
        <label className="block">
          <span className="mb-1 block text-meta text-tg-hint">TRON address</span>
          <input className={`${FIELD} w-full font-mono`} placeholder="T…" value={address} spellCheck={false} autoComplete="off" onChange={(e) => setAddress(e.target.value)} />
          {!shapeOk && (
            <StateLine tone="bad" className="mt-1" more="A TRON address is 34 characters and starts with T. Copy the whole address from its source.">
              {trimmed.startsWith('T') ? `${trimmed.length} characters — a TRON address has 34.` : 'Does not start with T.'}
            </StateLine>
          )}
        </label>
        <label className="block">
          <span className="mb-1 block text-meta text-tg-hint">Label (optional)</span>
          <input className={`${FIELD} w-full`} placeholder="settlement account" value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
      </div>
      {m.isError && <StateLine tone="bad" className="mt-2" more={textOf(m.error)}>Not proposed — try again.</StateLine>}
      <div className="mt-3 flex gap-2">
        <button
          className="btn-primary min-h-[44px] flex-1 text-body"
          disabled={!partySlug || trimmed === '' || !shapeOk || m.isPending}
          onClick={() => m.mutate()}
        >
          {m.isPending ? 'Proposing…' : 'Propose'}
        </button>
        <button className="btn-secondary min-h-[44px] text-body" onClick={onDone}>Cancel</button>
      </div>
      <MoreDetails label="What happens next">
        <p className="text-meta text-tg-hint">Accepted only from the desk's allowed IP. The Books approve it (Approve payout address); only then can it be paid, and every payout still needs your Ledger. It is screened in the background and the verdict shows here.</p>
      </MoreDetails>
    </Card>
  );
}

export function Whitelist() {
  const [proposing, setProposing] = useState(false);
  const [paying, setPaying] = useState<WhitelistEntry | null>(null);
  // A payment the sheet saw recorded: the list keeps reading until that address's stage is seen to move (re-review #2).
  const [expected, setExpected] = useState<StageExpected>(null);
  const qc = useQueryClient();
  // While a test is out, or a recorded payment's stage is not yet seen, the list is read again — a landed test (and
  // its Received) shows without a reload (#4).
  const q = useQuery({ queryKey: ['custody', 'whitelist'], queryFn: api.whitelist, refetchInterval: (query) => whitelistPollMs(query.state.data?.entries, expected) });
  useEffect(() => {
    if (expected !== null && !expectedPending(q.data?.entries, expected)) setExpected(null);
  }, [q.data, expected]);

  const state = listState(q);
  const entries = q.data?.entries ?? [];
  // The test's figure (db/0098), from the caps row; 10 USDT from an older server.
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, staleTime: 30_000, retry: false });
  const testUsdt = limits.data?.testPaymentUsdt ?? '10';
  const groups: Array<[WhitelistGroup, string]> = [['unknown', 'Not known'], ['awaiting', 'Awaiting the Books'], ['approved', 'Approved'], ['test_sent', 'Test sent'], ['payable', 'Whitelisted'], ['unavailable', 'Unavailable']];
  // One gold Received on the screen (#7): the first landed test, in the order the groups are shown.
  const ordered = groups.flatMap(([g]) => entries.filter((e) => whitelistGroup(e) === g));
  const primaryId = primaryReceivedId(ordered, { formOpen: proposing });
  const failed = <ReadFailed what="the address book" error={q.error} onRetry={() => void q.refetch()} pending={q.isFetching} />;

  return (
    <div className="space-y-3">
      {/* Mounted whatever the list does: a failed refresh must not take a queued payout's sheet with it. */}
      <MoveSheet
        open={paying !== null}
        onClose={() => setPaying(null)}
        title="Pay"
        verb="Pay"
        destination={paying ? { id: paying.id, name: paying.partyName, address: paying.address, stage: paying.stage ?? null, testPaymentUsdt: testUsdt } : null}
        sources={paying ? paySources(paying) : ['running_hot']}
        onRecorded={(toWalletId) => { setExpected({ id: toWalletId, since: Date.now() }); void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] }); }}
        about="A payout to this approved address — from Hot, or for a merchant also from Merchant Hot or Flagged. The desk builds the transaction and offers it in Approvals for your Ledger; nothing moves until you sign it there."
      />

      {proposing ? (
        <ProposeForm onDone={() => setProposing(false)} />
      ) : (
        <button
          className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-dashed border-tg-secondary px-4 text-body font-medium text-tg-hint lg:border-stroke"
          onClick={() => setProposing(true)}
        >
          <Plus className="h-4 w-4" /> Propose a payout address
        </button>
      )}

      {state.show === 'failed' ? failed : state.show === 'pending' ? <Pending what="Reading…" /> : (
        <>
          {state.stale && failed}

          {/* The stages in the order an address goes through them (db/0098): Approved → Test sent → Whitelisted. */}
          {groups.map(([group, heading]) => {
            const rows = entries.filter((e) => whitelistGroup(e) === group);
            return rows.length === 0 ? null : (
              <div key={group} className="space-y-3">
                <h3 className="pt-1 text-label font-semibold uppercase tracking-wide text-tg-hint">{heading}</h3>
                {rows.map((e) => <Entry key={e.id} e={e} onPay={setPaying} testUsdt={testUsdt} primary={e.id === primaryId} />)}
              </div>
            );
          })}

          {entries.length === 0 && (
            <Card><EmptyState>No payout addresses proposed yet.</EmptyState></Card>
          )}
        </>
      )}
    </div>
  );
}
